import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService"; // Import the common API service
import icons from "../../../assets/images/constantIcons";
import { Link } from "react-router-dom";
import images from "../../../assets/images/constantImages";
import DeletePopup from "./DeletePopup";
import toast from "react-hot-toast";
import { UserContext } from "../../Layout/UserContext";

const useAddStaff = () => {
  const [staffData, setStaffData] = useState([]); // To store the staff fetched data
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [rowsPerPage, setRowsPerPage] = useState(15); // Track rows per page
  const [deletePopupIndex, setDeletePopupIndex] = useState(null);
  const [filterType, setFilterType] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const { triggerNotificationRefresh } = useContext(UserContext);

  const handleDeletePopup = (id) => {
    if (id !== undefined) {
      setDeletePopupIndex(id);
    }
  };

  // Fetch staff data
  const fetchStaffData = useCallback(async () => {
    setLoading(true)
    try {
      let url = "/staff_users";
      const params = new URLSearchParams();
      if (filterType === "active") params.append("type", "active");
      if (filterType === "deactive") params.append("type", "deactive");
      if (searchQuery) params.append("search", searchQuery);

      const response = await apiService.get(`${url}?${params.toString()}`);
      setStaffData(response.data);
      triggerNotificationRefresh();
    } catch (errors) {
      handleError(errors)
    } finally {
      setLoading(false)
    }
  }, [filterType, searchQuery]);

  useEffect(() => {
    fetchStaffData();
  }, [filterType, searchQuery]);

  // Check in function

  const handleCheckIn = async (staffId) => {

    try {
      const formData = new FormData();
      formData.append("staff_user_id", staffId);
      const response = await apiService.post("attendance_histories/check_in", formData);
      toast.success(response.message, { duration: 2000 })
      fetchStaffData();
    } catch (errors) {
      handleError(errors)
    }
  }

  const handleCheckOut = async (staffId) => {
    try {
      const response = await apiService.put(`attendance_histories/check_out?staff_user_id=${staffId}`);

      toast.success(response.message, { duration: 2000 });
      fetchStaffData();
    } catch (errors) {
      handleError(errors);
    }
  };

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value }) => <div className="w-[120px]">{value}</div>,
      },
      {
        Header: "Mobile",
        accessor: "mobile",
        Cell: ({ value }) => <div className="text-nowrap">{value}</div>,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => <div className="text-nowrap">{value}</div>,
      },
      {
        Header: "Joining date",
        accessor: "joiningdate",
      },

      {
        Header: "Role",
        accessor: "role_id",
        headerAlign: "text-center",
        cellAlign: "text-center",
        Cell: ({ value }) => {
          return (
            <p className="text-xs sm:text-sm font-medium bg-[#F53FA11A] text-tertiary rounded-3xl md:w-[120px] break-words px-4 py-[6px] text-center">
              {value}
            </p>
          );
        },
      },

      {
        Header: "Check In / Out",
        accessor: "checkin_out",
        headerAlign: "text-center",
        cellAlign: "text-center",
        disableSortBy: true,
        Cell: ({ row }) => {
          const staffId = row.original.id;
          const checkINStatus = row.original.checkin_status;

          return (
            <div className="flex justify-center gap-3">
              {/* Check In */}
              <div className="relative group">
                {checkINStatus === "No" && (
                  <button
                    onClick={() => handleCheckIn(staffId)}
                    className="w-8 h-8 flex justify-center items-center gap-2 bg-blueButton  text-white  px-2 py-2 text-sm  rounded-3xl "
                  >
                    <img src={images.chechin} alt="chechin" className="" />
                    <span className="w-[80px] absolute left-1/2 bottom-full mb-1 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 font-bold text-white bg-textblue  text-xs rounded py-1 px-2 transition-opacity">
                      Check In
                    </span>
                  </button>
                )}

                {checkINStatus === "Yes" && (
                  <button
                    onClick={() => handleCheckOut(staffId)}
                    className="w-8 h-8 flex items-center gap-2 bg-red  text-white  px-2 py-2 text-sm  rounded-3xl "
                  >
                    <img src={images.chechin} alt="cheout" className="rotate-180" />
                    <span className="w-[90px] absolute left-1/2 bottom-full mb-1 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 font-bold text-white bg-red  text-xs rounded py-1 px-2 transition-opacity">
                      Check Out
                    </span>
                  </button>
                )}
              </div>
            </div>
          )
        }
      },
      {
        Header: "Attendance",
        accessor: "attendance_status",
        headerAlign: "text-center",
        Cell: ({ value }) => {
          return (
            <div className="flex justify-center">
              <p
                className={`text-xs sm:text-sm font-medium flex justify-center items-center rounded-3xl w-[80px] px-4 py-[6px]  ${value === "Present"
                  ? "text-textblue bg-textblue bg-opacity-[10%]" : "text-red bg-red bg-opacity-[10%]"}`}
              >
                {value}
              </p>
            </div>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        headerAlign: "text-center",
        cellAlign: "text-center",
        disableSortBy: true,
        Cell: ({ value }) => (
          <div className="flex items-center justify-center">
            <p
              className={`text-xs sm:text-sm font-medium flex items-center justify-center rounded-3xl w-[80px]  px-4 py-[6px]
              ${value === "Active"
                  ? "text-purple bg-purple bg-opacity-[10%]"
                  : "text-red bg-red bg-opacity-[10%]"
                }`}
            >
              {value}
            </p>
          </div>
        ),
      },

      {
        Header: "Action",
        accessor: "action",
        headerAlign: "text-center",
        cellAlign: "text-center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div className="flex justify-start md:justify-center items-center gap-5">
              <Link
                to={{
                  pathname: `/mainDashboard/staff/viewStaff/${row.original.id}`,
                  state: { status: row.original.status }, // ✅ Pass status in states
                }}
              >
                <div className="relative group">
                  <div className="p-2 bg-green50 text-green rounded-full w-[40px] h-[40px] cursor-pointer flex items-center justify-center">
                    <icons.IoEyeOutline className="text-lg font-bold" />
                  </div>
                </div>
              </Link>
              <div
                className="flex items-center justify-center focus:outline-none  rounded-full p-2 cursor-pointer w-[50px]"
                onClick={() =>
                  handleDeletePopup(row.original.id, row.original.status)
                } // Handle status toggle
              >
                {row.original.status === "Active" ? (
                  <img src={images.Active} alt="Active" className="" />
                ) : (
                  <img src={images.Deactive} alt="Deactive" className="" />
                )}
              </div>

              {deletePopupIndex === row.original.id && (
                <DeletePopup
                  isOpen={deletePopupIndex === row.original.id}
                  onClose={() => handleDeletePopup(null)}
                  staffName={row.original.name}
                  staffId={row.original.id}
                  fetchStaffData={fetchStaffData}
                  isActive={
                    staffData.find((staff) => staff.id === row.original.id)
                      ?.status === "active"
                  }
                />
              )}
            </div>
          );
        },
      },
    ],
    [deletePopupIndex, staffData]
  );

  // data of customer
  const data = useMemo(() => {
    if (!Array.isArray(staffData)) return []; // Safeguard in case customers is not an array
    return staffData?.map((staff) => ({
      id: staff.id,
      unique_id: staff.unique_id || "abc123",
      name: staff.full_name,
      email: staff.email,
      mobile: staff.mobile_no,
      joiningdate: staff.joining_date,
      attendance_status: staff.attendance_status === "present" ? "Present" : "Absent",
      role_id: staff.role ? staff.role.name : "N/A",
      status: staff.status === "active" ? "Active" : "Deactive",
      checkin_status: staff.checkin_status,
      action: "",
    }));
  }, [staffData]);

  // Paginate the data
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to page 1 when rows per page change
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    fetchStaffData,
    setFilterType,
    filterType,
    searchQuery,
    setSearchQuery,
    loading
  };
};

export default useAddStaff;
